import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./css/Tearsheet.css";
import * as XLSX from "xlsx-js-style";

export default function NotesAndCommentary({ productId, vendorId, productName, vendorName, contextPath, currentUser }) {
    const subject =
        productId ? { kind: "product", id: productId } :
            vendorId  ? { kind: "vendor",  id: vendorId  } : null;

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const [text, setText] = useState("");
    const [shareScope, setShareScope] = useState("ME");
    const [editingId, setEditingId] = useState(null);
    const accent = "var(--rusty-orange, #e87722)";

    const SCOPE_TO_SHARE_ID = {
        ME: 1,
        COMPANY: 2,
        COMMUNITY: 3,
    };


    const shareKeyOf = (n) => {
        if (n.shareScope) return n.shareScope;                  // "ME" | "COMPANY" | "COMMUNITY"
        if (n.shareId === 1) return "ME";
        if (n.shareId === 2) return "COMPANY";
        if (n.shareId === 3) return "COMMUNITY";
        return "ME";
    };

    const viewableByText = (n) => {
        if (n.viewableBy) return n.viewableBy;
        const scope = shareKeyOf(n);
        if (scope === "ME") return n.authorName;
        if (scope === "COMPANY") return "3di";
        return "Profiler Community";
    };

    const [filterBy, setFilterBy] = useState("ME");
    const counts = useMemo(() => {
        const c = { ME: 0, COMPANY: 0, COMMUNITY: 0 };
        for (const n of notes) {
            const k = shareKeyOf(n);
            if (c[k] !== undefined) c[k] += 1;
        }
        return { ...c, ALL: notes.length };
    }, [notes]);

    const filteredNotes = useMemo(
        () => notes.filter(n => (filterBy === "ALL" ? true : shareKeyOf(n) === filterBy)),
        [notes, filterBy]
    );


    const authHeaders = () => {
        const t = localStorage.getItem("token");
        return t ? { Authorization: `Bearer ${t}` } : {};
    };

    // Current user (for labels + disabling share controls)
    const [me, setMe] = useState(currentUser || null);
    useEffect(() => {
        let mounted = true;
        async function fetchMe() {
            if (currentUser) return;
            try {
                const res = await axios.get(`${contextPath}/me`, { headers: authHeaders() });
                if (mounted) setMe(res.data);
            } catch {
                // non-fatal
            }
        }
        fetchMe();
        return () => { mounted = false; };
    }, [contextPath, currentUser]);

    // Fetch notes for product or vendor
    useEffect(() => {
        if (!subject) return;
        let mounted = true;
        setLoading(true);
        setErr(null);
        axios.get(`${contextPath}/${subject.kind}/${subject.id}/notes`, { headers: authHeaders() })
            .then(res => { if (mounted) setNotes(res.data || []); })
            .catch(e => { if (mounted) setErr(e); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [contextPath, subject?.kind, subject?.id]);


    function exportNotesToExcel() {
        const BLUE  = "25408F";           // --bs-btn-bg
        const WHITE = "FFFFFF";
        const COLS  = ["A","B","C","D","E"];

        const subjectName =
            subject?.name || productName || vendorName || `Item ${subject?.id ?? ""}`.trim();

        const subjectKind = (subject?.kind || "").toLowerCase();
        const nameHeader =
            subjectKind === "vendor" || (!!vendorName && !productName)
                ? "Vendor Name"
                : subjectKind === "product" || (!!productName && !vendorName)
                    ? "Product Name"
                    : "Item Name";

        const title = `Notes and Commentary for ${subjectName}`;

        const rowsSource =
            (typeof filteredNotes !== "undefined" ? filteredNotes : notes) || [];

        const headerRow = [nameHeader, "Date & Time", "User", "Notes", "Viewable By"];

        // keep Date object for Excel time; keep \n so wrapping works
        const rows = rowsSource.map((n) => [
            subjectName,
            new Date(n.updatedAt || n.createdAt),
            n.authorName,
            String(n.text ?? "").replace(/\r\n/g, "\n"),
            viewableByText(n),
        ]);

        const aoa = [[title], [], headerRow, ...rows];
        const ws  = XLSX.utils.aoa_to_sheet(aoa);

        // merge title
        ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];

        // widths
        ws["!cols"] = [{ wch: 36 }, { wch: 20 }, { wch: 22 }, { wch: 90 }, { wch: 24 }];

        // freeze pane under headers
        ws["!freeze"] = { xSplit: 0, ySplit: 3, topLeftCell: "A4", activePane: "bottomLeft", state: "frozen" };

        // title style (large, blue)
        if (ws.A1) {
            ws.A1.s = {
                font: { bold: true, sz: 20, color: { rgb: BLUE } },
                alignment: { horizontal: "left", vertical: "center", wrapText: true },
            };
        }

        // header row style (forced blue background + white text)
        const headerStyle = {
            font: { bold: true, color: { rgb: WHITE } },
            fill: { patternType: "solid", fgColor: { rgb: BLUE }, bgColor: { rgb: BLUE } },
            alignment: { horizontal: "left", vertical: "center", wrapText: true },
        };
        ["A3","B3","C3","D3","E3"].forEach(addr => { if (ws[addr]) ws[addr].s = headerStyle; });

        // base cell style for all data cells: LEFT + WRAP
        const baseCell = { alignment: { horizontal: "left", vertical: "top", wrapText: true } };

        // apply base style to all populated cells in the data area
        const startRow = 4; // first data row
        for (let r = 0; r < rows.length; r++) {
            for (let c = 0; c < COLS.length; c++) {
                const addr = `${COLS[c]}${startRow + r}`;
                if (ws[addr]) {
                    ws[addr].s = { ...(ws[addr].s || {}), ...baseCell };
                }
            }
            // date-time number format for column B
            const bAddr = `B${startRow + r}`;
            if (ws[bAddr]) {
                ws[bAddr].s = { ...(ws[bAddr].s || {}), numFmt: "dd-mm-yyyy hh:mm" };
                ws[bAddr].z = "dd-mm-yyyy hh:mm";
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Notes");

        const safe = (subjectName || "Item").replace(/[\\/:*?"<>|]/g, "");
        XLSX.writeFile(wb, `3di${safe}Notes.xlsx`, { cellDates: true });
    }


    function resetForm() {
        setText("");
        setEditingId(null);
    }

    async function handleSubmit() {
        if (!text.trim() || !subject) return;

        if (shareScope !== "ME") {
            const ok = window.confirm("This note will be visible beyond just you. Proceed?");
            if (!ok) return;
        }

        try {
            const headers = authHeaders();
            const shareId = SCOPE_TO_SHARE_ID[shareScope] ?? 1;

            const body = { text, shareScope, shareId };

            if (editingId) {
                const { data: updated } = await axios.put(
                    `${contextPath}/notes/${editingId}`,
                    body,
                    { headers }
                );
                setNotes(prev => prev.map(n => (n.id === editingId ? updated : n)));
            } else {
                const { data: created } = await axios.post(
                    `${contextPath}/${subject.kind}/${subject.id}/notes`,
                    body,
                    { headers }
                );
                setNotes(prev => [created, ...prev]);
            }

            resetForm();
        } catch (e) {
            alert("Sorry, saving note failed.");
            console.error(e);
        }
    }

    async function handleDelete(id) {
        if (!window.confirm("Are you sure you want to delete this note?")) return;
        try {
            await axios.delete(`${contextPath}/notes/${id}`, { headers: authHeaders() });
            setNotes(prev => prev.filter(n => n.id !== id));
            if (editingId === id) resetForm();
        } catch (e) {
            alert("Delete failed.");
            console.error(e);
        }
    }

    function startEdit(note) {
        setEditingId(note.id);
        setText(note.text || "");
        setShareScope(note.shareScope || "ME");
    }

    const shareLabels = {
        ME: me?.fullName ? `${me.fullName}` : "Me",
        COMPANY: "3di",
        COMMUNITY: "Profiler Community",
    };


    // Legacy rule: disable sharing beyond ME for special company
    const shareDisabled = me?.companyId === 164;

    // Guard for missing ids
    if (!subject) {
        return <div className="text-sm text-red-600">Notes module needs a productId or vendorId.</div>;
    }

    return (
        <section className="mb-6 Threedi-blue">
            <h3 className="tearsheet-heading">Notes &amp; Commentary</h3>

            {/* Viewable By */}
            <div className="mt-3 mb-2 flex items-center gap-6 text-sm">
                <div className="font-bold">Viewable by:</div>

                <label className="inline-flex items-center gap-2">
                    <input
                        type="radio"
                        name="shareScope"
                        value="ME"
                        disabled={shareDisabled}
                        checked={shareScope === "ME"}
                        onChange={() => setShareScope("ME")}
                    />
                    <span>{shareLabels.ME}</span>
                </label>

                <label className="inline-flex items-center gap-2">
                    <input
                        type="radio"
                        name="shareScope"
                        value="COMPANY"
                        disabled={shareDisabled}
                        checked={shareScope === "COMPANY"}
                        onChange={() => setShareScope("COMPANY")}
                    />
                    <span>{shareLabels.COMPANY}</span>
                </label>

                <label className="inline-flex items-center gap-2">
                    <input
                        type="radio"
                        name="shareScope"
                        value="COMMUNITY"
                        disabled={shareDisabled}
                        checked={shareScope === "COMMUNITY"}
                        onChange={() => setShareScope("COMMUNITY")}
                    />
                    <span>{shareLabels.COMMUNITY}</span>
                </label>
            </div>

            {/* Editor */}
            <div className="flex gap-4">
        <textarea
            id="comment"
            name="comment"
            placeholder="Insert note"
            rows={5}
            className="w-full border rounded p-2 text-sm resize-none"
            value={text}
            onChange={e => setText(e.target.value)}
        />
                <div className="flex flex-col gap-2 min-w-[180px]">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-3 py-2 rounded-2xl font-semibold border text-white"
                        style={{ backgroundColor: accent, borderColor: accent }}
                        title={editingId ? "Save changes" : "Submit Note"}
                    >
                        {editingId ? "Save Changes" : "Submit Note"}
                    </button>

                    {editingId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-3 py-2 rounded-2xl font-semibold border"
                            title="Cancel editing"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* Notes list */}
            <div className="mt-6">
                {loading && <div className="text-sm text-gray-500">Loading notes…</div>}
                {err && <div className="text-sm text-red-600">Failed to load notes.</div>}
                {!loading && notes.length === 0 && <div className="text-sm text-gray-500">No notes yet.</div>}

                {/* Filter Notes By */}
                <div className="mt-4 mb-2 text-sm flex items-center gap-4">
                    <span className="font-bold">Filter Notes By :</span>

                    <label className="inline-flex items-center gap-2">
                        <input
                            type="radio"
                            name="filterBy"
                            value="ME"
                            checked={filterBy === "ME"}
                            onChange={() => setFilterBy("ME")}
                        />
                        <span>{me?.fullName || "User"} ({counts.ME})</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                        <input
                            type="radio"
                            name="filterBy"
                            value="COMPANY"
                            checked={filterBy === "COMPANY"}
                            onChange={() => setFilterBy("COMPANY")}
                        />
                        <span>3di ({counts.COMPANY})</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                        <input
                            type="radio"
                            name="filterBy"
                            value="COMMUNITY"
                            checked={filterBy === "COMMUNITY"}
                            onChange={() => setFilterBy("COMMUNITY")}
                        />
                        <span>Profiler Community ({counts.COMMUNITY})</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                        <input
                            type="radio"
                            name="filterBy"
                            value="ALL"
                            checked={filterBy === "ALL"}
                            onChange={() => setFilterBy("ALL")}
                        />
                        <span>All ({counts.ALL})</span>
                    </label>
                </div>

                {/* Notes table */}
                {!loading && filteredNotes.length === 0 ? (
                    <div className="text-sm text-gray-500">No notes yet.</div>
                ) : (
                    <div className="max-h-[420px] overflow-auto rounded border">

                        <table className="w-full text-sm notes-table" style={{ borderSpacing: 0 }}>
                            <thead>
                            <tr  className="threedi-blue-bg">
                                <th className="p-2 text-left">Date</th>
                                <th className="p-2 text-left">User</th>
                                <th className="p-2 text-left">Note</th>
                                <th className="p-2 text-left">Viewable By</th>
                                <th className="p-2"></th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredNotes.map((n) => (
                                <tr key={n.id} >
                                    <td className="p-2 align-top">
                                        {new Date(n.updatedAt || n.createdAt).toLocaleString("en-GB")}
                                    </td>
                                    <td className="p-2 align-top">{n.authorName}</td>
                                    <td className="p-2 align-top whitespace-pre-wrap">{n.text}</td>
                                    <td className="p-2 align-top">{viewableByText(n)}</td>
                                    <td className="p-2 align-top text-right">
                                        {n.editable && (
                                            <div className="flex gap-3 justify-end">
                                                <button className="underline" onClick={() => startEdit(n)} title="Edit">
                                                    Edit
                                                </button>
                                                <button className="underline" onClick={() => handleDelete(n.id)} title="Delete">
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                        <div className="flex items-center gap-2 justify-end">
                            <button
                                type="button"
                                onClick={exportNotesToExcel}
                                className="inline-flex items-center gap-2 px-2 py-1 rounded text-sm bg-white hover:bg-gray-100"
                                title="Export Notes to Excel"
                            >
                                {/* Excel-looking SVG */}
                                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                                    <path d="M4 4h9a1 1 0 0 1 1 1v3h5a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2H10l-6-1.5V5a1 1 0 0 1 1-1z" fill="#107C41"/>
                                    <path d="M10 8h10v10H10z" fill="#33C481"/>
                                    <path d="M7.5 9l1.9 3-1.9 3h1.7l1.1-1.9L11.4 15h1.7l-1.9-3 1.9-3h-1.7l-1.1 1.9L9.2 9H7.5z" fill="#fff"/>
                                </svg>
                                Export
                            </button>
                        </div>

                    </div>
                )}
            </div>
        </section>
    );
}
